import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { getConfig } from '../config';

export enum CampaignStatus {
  DRAFT = 'Draft',
  PUBLISHED = 'Published',
}

export enum CampaignFilterStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
  ALL = 'all',
}

export interface Campaign {
  id: number;
  name: string;
  status: CampaignStatus;
  startAt: Date;
  endAt: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CampaignResponse {
  id: number;
  name: string;
  status: CampaignStatus;
  startAt: string;
  endAt: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCampaignRequest {
  name: string;
  startAt: Date;
  endAt: Date;
  status?: CampaignStatus;
}

export interface UpdateCampaignRequest {
  name?: string;
  startAt?: Date;
  endAt?: Date;
}

export interface UpdateCampaignStatusRequest {
  status: CampaignStatus;
}

export interface ListCampaignsParams {
  status?: CampaignFilterStatus;
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface ListCampaignsResponse {
  campaigns: Campaign[];
  total: number;
  lastEvaluatedKey?: string;
}

export class CampaignClient {
  private readonly headers: Record<string, string>;

  constructor(private readonly apiUrl: string) {
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Origin: window.location.origin,
      'x-api-key': getConfig().apiKey,
    };
  }

  private transformCampaignResponse(response: CampaignResponse): Campaign {
    const campaign: Campaign = {
      id: response.id,
      name: response.name,
      status: response.status,
      startAt: new Date(response.startAt),
      endAt: new Date(response.endAt),
    };

    if (response.createdAt) {
      campaign.createdAt = new Date(response.createdAt);
    }

    if (response.updatedAt) {
      campaign.updatedAt = new Date(response.updatedAt);
    }

    return campaign;
  }

  private transformCampaignRequest(campaign: CreateCampaignRequest | UpdateCampaignRequest): any {
    const request: any = {};

    if ('name' in campaign && campaign.name !== undefined) {
      request.name = campaign.name;
    }
    if ('startAt' in campaign && campaign.startAt !== undefined) {
      request.startAt = campaign.startAt.toISOString();
    }
    if ('endAt' in campaign && campaign.endAt !== undefined) {
      request.endAt = campaign.endAt.toISOString();
    }
    if ('status' in campaign && campaign.status !== undefined) {
      request.status = campaign.status;
    }

    return request;
  }

  public async createCampaign(request: CreateCampaignRequest): Promise<Campaign> {
    try {
      const response: AxiosResponse<CampaignResponse> = await axios.post(
        `${this.apiUrl}/campaigns`,
        this.transformCampaignRequest(request),
        {
          headers: this.headers,
        },
      );

      return this.transformCampaignResponse(response.data);
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  public async listCampaigns(params?: ListCampaignsParams): Promise<ListCampaignsResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Filter by status using CampaignFilterStatus enum (active, inactive, draft, all)
      if (params?.status && Object.values(CampaignFilterStatus).includes(params.status)) {
        queryParams.append('status', params.status);
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.lastEvaluatedKey) {
        queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
      }

      const url = `${this.apiUrl}/campaigns${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response: AxiosResponse<{
        campaigns: CampaignResponse[];
        total: number;
        lastEvaluatedKey?: string;
      }> = await axios.get(url, {
        headers: this.headers,
      });

      const result: ListCampaignsResponse = {
        campaigns: response.data.campaigns.map((campaign) => this.transformCampaignResponse(campaign)),
        total: response.data.total,
      };

      if (response.data.lastEvaluatedKey) {
        result.lastEvaluatedKey = response.data.lastEvaluatedKey;
      }

      return result;
    } catch (error) {
      console.error('Error listing campaigns:', error);
      throw error;
    }
  }

  public async getCampaign(id: number): Promise<Campaign | null> {
    try {
      const response: AxiosResponse<CampaignResponse> = await axios.get(`${this.apiUrl}/campaigns/${id}`, {
        headers: this.headers,
      });

      return this.transformCampaignResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error getting campaign:', error);
      throw error;
    }
  }

  public async updateCampaign(id: number, request: UpdateCampaignRequest): Promise<Campaign | null> {
    try {
      const response: AxiosResponse<CampaignResponse> = await axios.put(
        `${this.apiUrl}/campaigns/${id}`,
        this.transformCampaignRequest(request),
        {
          headers: this.headers,
        },
      );

      return this.transformCampaignResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error updating campaign:', error);
      throw error;
    }
  }

  public async updateCampaignStatus(id: number, status: CampaignStatus): Promise<Campaign | null> {
    try {
      const response: AxiosResponse<CampaignResponse> = await axios.patch(
        `${this.apiUrl}/campaigns/${id}/status`,
        { status },
        {
          headers: this.headers,
        },
      );

      return this.transformCampaignResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error updating campaign status:', error);
      throw error;
    }
  }

  public async deleteCampaign(id: number): Promise<boolean> {
    try {
      await axios.delete(`${this.apiUrl}/campaigns/${id}`, {
        headers: this.headers,
      });

      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      console.error('Error deleting campaign:', error);
      throw error;
    }
  }
}
